import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Copy, Loader2, Send, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useReferrals } from "@/components/invite/RewardWatcher";
import { InviteList } from "@/components/invite/InviteList";
import { InviteQR } from "@/components/invite/InviteQR";
import { useAuth } from "@/context/AuthContext";

const parse = (s) => [...new Set(s.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean))];

const Meter = ({ progress, per }) => (
  <div className="flex gap-1.5" data-testid="invite-progress-meter">
    {Array.from({ length: per }, (_, i) => (
      <span key={i} className={`h-2 w-8 rounded-full transition-colors duration-500 ${i < progress ? "bg-[#F59E0B]" : "bg-[#2D3442]"}`} data-testid={`invite-meter-step-${i + 1}`} />
    ))}
  </div>
);

const LinkRow = ({ link }) => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link); setCopied(true); toast.success("Invite link copied"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Couldn't copy. Select the link and copy it yourself."); }
  };
  const canShare = typeof navigator !== "undefined" && !!navigator.share;
  const share = async () => {
    try { await navigator.share({ title: "Join me on LoreRight", text: "I build my worlds on LoreRight. Join through my link for a 14-day Pro trial.", url: link }); }
    catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open sharing. Copy the link instead."); }
  };
  return (
    <div className="flex flex-wrap gap-2 sm:flex-nowrap">
      <Input readOnly value={link} onFocus={(e) => e.target.select()} className="min-w-0 basis-full font-mono text-xs sm:basis-auto" data-testid="invite-link-input" />
      <Button variant="outline" onClick={copy} className="shrink-0" data-testid="invite-copy-button">{copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}{copied ? "Copied" : "Copy"}</Button>
      <InviteQR link={link} name={user?.name || "A friend"} />
      {canShare && <Button onClick={share} className="shrink-0" data-testid="invite-share-button"><Share2 className="mr-1 h-4 w-4" />Share</Button>}
    </div>
  );
};

const EmailForm = ({ left }) => {
  const qc = useQueryClient();
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const list = parse(emails);
  const send = async (e) => {
    e.preventDefault();
    if (list.length > 10) return toast.error("Up to 10 emails at a time");
    setBusy(true);
    try {
      const { data } = await api.post("/referrals/invite", { emails: list, note });
      if (data.sent.length) toast.success(`Invite sent to ${data.sent.length} ${data.sent.length === 1 ? "friend" : "friends"}`);
      if (data.already_members.length) toast.info(`Already on LoreRight: ${data.already_members.join(", ")}`);
      if (data.skipped_self) toast.info("You can't invite yourself");
      if (data.failed?.length) toast.error(`Couldn't send to ${data.failed.join(", ")}. Try those again in a minute.`);
      if (data.over_limit.length) toast.warning(`Daily limit reached. Not sent: ${data.over_limit.join(", ")}`);
      if (data.sent.length) { setEmails(""); setNote(""); }
      qc.invalidateQueries({ queryKey: ["referrals"] });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={send} className="space-y-3" data-testid="invite-email-form">
      <Textarea value={emails} onChange={(e) => setEmails(e.target.value)} rows={2} placeholder="friend@example.com, partner@example.com" data-testid="invite-emails-input" />
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} placeholder="Add a personal note (optional)" data-testid="invite-note-input" />
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-[#7A7468]" data-testid="invite-sends-left">{list.length > 10 ? "Up to 10 emails at a time" : `${left} invites left today`}</span>
        <Button type="submit" disabled={busy || !list.length || list.length > 10 || !left} data-testid="invite-send-button">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Send invites</Button>
      </div>
    </form>
  );
};

export const InviteCard = () => {
  const { data, isLoading } = useReferrals();
  const scrolled = useRef(false);
  useEffect(() => {
    if (!data || scrolled.current || window.location.hash !== "#invite") return;
    scrolled.current = true;
    document.getElementById("invite")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [data]);
  if (isLoading || !data) return <p className="text-sm text-[#A8A090]">Loading…</p>;
  return (
    <div className="space-y-6" data-testid="invite-card">
      <p className="text-sm text-[#A8A090]">Friends who join through you get a 14-day Pro trial. Every {data.per_reward} friends who confirm their email earns you a free month of Pro.</p>
      <div className="flex flex-wrap items-center gap-4">
        <Meter progress={data.progress} per={data.per_reward} />
        <span className="text-sm text-[#F3EBDD]" data-testid="invite-progress-text">{data.progress} of {data.per_reward} toward your next free month</span>
        {data.months > 0 && <span className="text-xs text-[#FBBF24]" data-testid="invite-months-earned">{data.months} {data.months === 1 ? "month" : "months"} earned</span>}
      </div>
      <div className="space-y-2"><div className="lf-label">Your invite link</div><LinkRow link={data.link} /></div>
      <div className="space-y-2"><div className="lf-label">Invite by email</div><EmailForm left={data.sends_left} /></div>
      <InviteList invites={data.invites} />
    </div>
  );
};
